import axios, { AxiosRequestConfig } from 'axios';
import { validate } from 'numeral';
// config
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------
// routing/routes

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/user/me',
    login: '/api/users/sign_in',
    register: '/api/users',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  job_position: {
    list: '/api/job_positions',
    show: (id:string) => `/api/job_positions/${id}`,
    update_publish: (id:string) => `/api/job_positions/${id}/update_publish`,
    update: (id:string) => `/api/job_positions/${id}`,
    create: '/api/job_positions',
    delete: (id:string) => `/api/job_positions/${id}`,
    show_public: (id:string) => `/api/job_positions/${id}/public`,
    generate_criteria: `/api/job_positions/generate_criteria`,
  },
  interview: {
    show: (id:string) => `/api/interviews/${id}`,
    start_message: (id:string) => `/api/interviews/${id}/start_message`,
  },
  applicant: {
    list: '/api/applicants',
    show: (id:string) => `/api/applicants/${id}`,
    delete: (id:string) => `/api/applicants/${id}`,
  },
  members: {
    list: '/api/members',
  },
  interview_questions: {
    create: (id:string) => `/api/job_positions/${id}/interview_questions`,
    get: (id:string) => `/api/job_positions/${id}/interview_questions`,
    all: (id:string) => `/api/job_positions/${id}/interview_questions/index_all`,
  },
  custom_questions: {
    index: '/api/custom_questions',
    create: '/api/custom_questions',
    update_status: (id:string) => `/api/custom_questions/${id}/update_status`
  },
  interview_rooms: {
    create: (id:string) => `/api/job_positions/${id}/interview_rooms`,
    show: (job_position_id:string, room_id:string) => `/api/job_positions/${job_position_id}/interview_rooms/${room_id}`,
    next: (job_position_id:string, room_id:string) => `/api/job_positions/${job_position_id}/interview_rooms/${room_id}/next`,
    start_interview: (job_position_id:string, room_id:string) => `/api/job_positions/${job_position_id}/interview_rooms/${room_id}/start_interview`,
    start_recording: (job_position_id:string, room_id:string) => `/api/job_positions/${job_position_id}/interview_rooms/${room_id}/start_recording`
  },
  user: {
    show: `/api/user/account`,
    update: `/api/user/account`,
    resend_confirmation: `/api/user/resend_confirmation`,
    stripe: `/api/user/stripe`,
    stripe_plan_confirm: `/api/user/stripe_plan_confirm`,
    invoices: `/api/user/invoices`,
    update_password: `/api/user/update_password`,
    cancel_plan: `/api/user/cancel_plan`
  },
  notification_settings: {
    index: `/api/notification_settings`,
    update: (id:string) => `/api/notification_settings/${id}`
  },
  users: {
    index: `/api/users`,
    invite: `/api/users/invite`,
    update_privilege: (id:string) => `/api/users/${id}/update_privilege`,
    destroy: (id:string) => `/api/users/${id}`
  },
  invitations:{
    validate: '/api/invitations/validate'
  },
  confirmations:{
    confirm: '/api/users/sign_up_confirm'
  },
  reset_password:{
    reset: '/api/users/reset_password',
    set_password: '/api/users/set_password'
  }
};
